/**
 * Admin Roles Manager (Super Admin)
 *
 * Grant platform admin, tenant admin, and workspace admin.
 *
 * All three go through the database function `elevate_admin`, which decides who
 * may grant and writes the membership and the role together. This screen writes
 * no table itself, and says "Granted" only when the function answers success.
 * - Platform admin: `addPlatformAdmin` (asks for the signed-in person's own password).
 * - Tenant admin / workspace admin: `elevateAdmin` (lib/delegationRpc).
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Building2,
  Briefcase,
  Plus,
  AlertCircle,
  CheckCircle,
  User,
} from 'lucide-react';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  findUserByEmail,
  addPlatformAdmin,
  listSuperAdmins,
  type SuperAdmin,
} from '../../lib/superAdminService';
import { elevateAdmin } from '../../lib/delegationRpc';

type AdminType = 'platform' | 'tenant' | 'workspace';

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

interface WorkspaceOption {
  id: string;
  name: string;
  tenant_id: string;
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return 'Unknown error';
}

export default function AdminRolesManager() {
  const currentUserId = useUserStore((s) => s.userId);
  const [platformAdmins, setPlatformAdmins] = useState<(SuperAdmin & { userEmail: string })[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<AdminType>('platform');

  // Platform: add by email
  const [platformEmail, setPlatformEmail] = useState('');
  const [platformPassword, setPlatformPassword] = useState('');
  const [platformAdding, setPlatformAdding] = useState(false);
  const [platformError, setPlatformError] = useState<string | null>(null);
  const [platformSuccess, setPlatformSuccess] = useState<string | null>(null);

  // Tenant admin: email + tenant
  const [tenantAdminEmail, setTenantAdminEmail] = useState('');
  const [tenantAdminTenantId, setTenantAdminTenantId] = useState('');
  const [tenantAdminAdding, setTenantAdminAdding] = useState(false);
  const [tenantAdminError, setTenantAdminError] = useState<string | null>(null);
  const [tenantAdminSuccess, setTenantAdminSuccess] = useState<string | null>(null);

  // Workspace admin: email + workspace
  const [workspaceAdminEmail, setWorkspaceAdminEmail] = useState('');
  const [workspaceAdminWorkspaceId, setWorkspaceAdminWorkspaceId] = useState('');
  const [workspaceAdminAdding, setWorkspaceAdminAdding] = useState(false);
  const [workspaceAdminError, setWorkspaceAdminError] = useState<string | null>(null);
  const [workspaceAdminSuccess, setWorkspaceAdminSuccess] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Shown under the password box so it is plain whose password is being asked for.
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    const supabase = getSupabaseClient();
    const errors: string[] = [];

    try {
      const [adminsResult, tenantsRes, workspacesRes] = await Promise.all([
        listSuperAdmins()
          .then((a) => ({ data: a, error: null as string | null }))
          .catch((e) => {
            console.error('[AdminRolesManager] listSuperAdmins failed:', e);
            return { data: [] as (SuperAdmin & { userEmail: string })[], error: `Platform admins: ${errorMessage(e)}` };
          }),
        supabase.from('tenants').select('id, name, slug').order('name'),
        supabase.from('workspaces').select('id, name, tenant_id').order('name'),
      ]);

      if (adminsResult.error) errors.push(adminsResult.error);
      else setPlatformAdmins(adminsResult.data);

      if (tenantsRes.error) errors.push(`Tenants: ${tenantsRes.error.message}`);
      else setTenants(tenantsRes.data || []);

      if (workspacesRes.error) errors.push(`Workspaces: ${workspacesRes.error.message}`);
      else setWorkspaces(workspacesRes.data || []);

      if (errors.length) setLoadError(errors.join('. '));
    } catch (e) {
      console.error(e);
      setLoadError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    getSupabaseClient()
      .auth.getUser()
      .then(({ data }) => setSignedInEmail(data?.user?.email ?? null))
      .catch(() => setSignedInEmail(null));
  }, [loadData]);

  const handleAddPlatformAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId || !platformEmail.trim() || !platformPassword) return;
    setPlatformAdding(true);
    setPlatformError(null);
    setPlatformSuccess(null);
    try {
      const user = await findUserByEmail(platformEmail.trim());
      if (!user) {
        setPlatformError('No user found with that email. They must have an account first.');
        return;
      }
      // The server only adds a platform admin for someone who signed in within
      // the last ten minutes, so the person at this screen re-enters their password.
      await addPlatformAdmin(user.id, platformPassword);
      setPlatformSuccess(`Granted platform admin to ${user.email}`);
      setPlatformEmail('');
      setPlatformAdmins(await listSuperAdmins());
    } catch (err) {
      // Show the server's own sentence. (A Supabase error is a plain object,
      // not an Error — the old check dropped it and said only "Failed to add
      // platform admin".)
      setPlatformError(errorMessage(err));
      console.error('[AdminRolesManager] Add platform admin failed:', err);
    } finally {
      setPlatformPassword('');
      setPlatformAdding(false);
    }
  };

  const handleGrantTenantAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId || !tenantAdminEmail.trim() || !tenantAdminTenantId) return;
    setTenantAdminAdding(true);
    setTenantAdminError(null);
    setTenantAdminSuccess(null);
    try {
      const user = await findUserByEmail(tenantAdminEmail.trim());
      if (!user) {
        setTenantAdminError('No user found with that email. They must have an account first.');
        return;
      }
      // The database function decides who may grant, and writes the membership
      // and the role together. "Granted" is shown only when it answers success;
      // a refusal comes back as a sentence and is shown as it is.
      const result = await elevateAdmin({
        targetUserId: user.id,
        adminRole: 'TENANT_ADMIN',
        scopeId: tenantAdminTenantId,
      });
      if (!result.success) {
        setTenantAdminError(result.error ?? 'The tenant admin grant was refused. Nothing was changed.');
        return;
      }
      const tenantName = tenants.find((t) => t.id === tenantAdminTenantId)?.name ?? 'the selected tenant';
      setTenantAdminSuccess(`Granted tenant admin of ${tenantName} to ${user.email}.`);
      setTenantAdminEmail('');
      setTenantAdminTenantId('');
    } catch (err) {
      setTenantAdminError(errorMessage(err));
      console.error('[AdminRolesManager] Grant tenant admin failed:', err);
    } finally {
      setTenantAdminAdding(false);
    }
  };

  const handleGrantWorkspaceAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId || !workspaceAdminEmail.trim() || !workspaceAdminWorkspaceId) return;
    setWorkspaceAdminAdding(true);
    setWorkspaceAdminError(null);
    setWorkspaceAdminSuccess(null);
    try {
      const user = await findUserByEmail(workspaceAdminEmail.trim());
      if (!user) {
        setWorkspaceAdminError('No user found with that email. They must have an account first.');
        return;
      }
      // Same function as the tenant grant. It also adds the person to the
      // workspace's tenant as a member, so they show in Settings → Users.
      const result = await elevateAdmin({
        targetUserId: user.id,
        adminRole: 'WORKSPACE_ADMIN',
        scopeId: workspaceAdminWorkspaceId,
      });
      if (!result.success) {
        setWorkspaceAdminError(result.error ?? 'The workspace admin grant was refused. Nothing was changed.');
        return;
      }
      const workspaceName =
        workspaces.find((w) => w.id === workspaceAdminWorkspaceId)?.name ?? 'the selected workspace';
      setWorkspaceAdminSuccess(`Granted workspace admin of ${workspaceName} to ${user.email}.`);
      setWorkspaceAdminEmail('');
      setWorkspaceAdminWorkspaceId('');
    } catch (err) {
      setWorkspaceAdminError(errorMessage(err));
      console.error('[AdminRolesManager] Grant workspace admin failed:', err);
    } finally {
      setWorkspaceAdminAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[var(--hover-tint)] rounded w-48" />
          <div className="h-32 bg-[var(--hover-tint)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-[var(--text-pri)]">Admin Roles</h1>
        <p className="text-[var(--text-ter)] mt-1">
          Grant platform, tenant, or workspace admin. Users must already exist (e.g. signed up or invited).
        </p>
      </div>

      {loadError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 flex items-center justify-between gap-4">
          <span className="text-[var(--coral)]">{loadError}</span>
          <button
            type="button"
            onClick={() => { setLoading(true); loadData(); }}
            className="shrink-0 px-3 py-1.5 bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)]"
          >
            Retry
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <button
          type="button"
          onClick={() => setActiveSection('platform')}
          className={`p-4  border-2 text-left transition-all ${
            activeSection === 'platform' ? 'border-[var(--coral)] bg-[var(--coral-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)]'
          }`}
        >
          <ShieldCheck className="h-6 w-6 text-[var(--coral)] mb-2" />
          <span className="font-medium text-[var(--text-pri)]">Platform Admins</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveSection('tenant')}
          className={`p-4  border-2 text-left transition-all ${
            activeSection === 'tenant' ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)]'
          }`}
        >
          <Building2 className="h-6 w-6 text-[var(--text-ter)] mb-2" />
          <span className="font-medium text-[var(--text-pri)]">Tenant Admins</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveSection('workspace')}
          className={`p-4  border-2 text-left transition-all ${
            activeSection === 'workspace' ? 'border-[var(--green)] bg-[var(--green-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)]'
          }`}
        >
          <Briefcase className="h-6 w-6 text-[var(--green)] mb-2" />
          <span className="font-medium text-[var(--text-pri)]">Workspace Admins</span>
        </button>
      </div>

      {activeSection === 'platform' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Grant Platform Admin</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">
            Platform admins can create tenants, manage all workspaces, and access Super Admin.
          </p>
          <form onSubmit={handleAddPlatformAdmin} className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="platform-admin-email" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Email of the person to add
              </label>
              <input
                id="platform-admin-email"
                type="email"
                value={platformEmail}
                onChange={(e) => setPlatformEmail(e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="platform-admin-own-password" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Your own sign-in password
              </label>
              <input
                id="platform-admin-own-password"
                type="password"
                value={platformPassword}
                onChange={(e) => setPlatformPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="The password you sign in with"
                aria-describedby="platform-admin-own-password-help"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500"
              />
              <p id="platform-admin-own-password-help" className="mt-1 text-sm text-[var(--text-ter)]">
                {signedInEmail
                  ? `The password for ${signedInEmail}, the account you are signed in with.`
                  : 'The password for the account you are signed in with.'}{' '}
                It confirms it is you. We never ask for the other person&rsquo;s password.
              </p>
            </div>
            <button
              type="submit"
              disabled={platformAdding || !platformEmail.trim() || !platformPassword}
              className="flex items-center gap-2 px-4 py-2 mt-6 bg-[var(--coral)] text-white hover:bg-[var(--coral)] disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {platformAdding ? 'Adding…' : 'Add Platform Admin'}
            </button>
          </form>
          {platformError && (
            <div className="mt-3 flex items-center gap-2 text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4" />
              {platformError}
            </div>
          )}
          {platformSuccess && (
            <div className="mt-3 flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)] text-sm">
              <CheckCircle className="h-4 w-4" />
              {platformSuccess}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-[var(--rule)]">
            <p className="text-sm text-[var(--text-ter)] mb-2">Current platform admins ({platformAdmins.length})</p>
            <ul className="text-sm text-[var(--text-sec)] space-y-1">
              {platformAdmins.map((a) => (
                <li key={a.id} className="flex items-center gap-2">
                  <User className="h-4 w-4 text-[var(--text-ter)]" />
                  {a.userEmail || a.userId}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {activeSection === 'tenant' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Grant Tenant Admin</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">
            Tenant admins can manage workspaces and users within one organization.
          </p>
          <form onSubmit={handleGrantTenantAdmin} className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">User email</label>
              <input
                type="email"
                value={tenantAdminEmail}
                onChange={(e) => setTenantAdminEmail(e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Tenant</label>
              <select
                value={tenantAdminTenantId}
                onChange={(e) => setTenantAdminTenantId(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select tenant</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
              {tenants.length === 0 && !loadError && (
                <p className="mt-2 text-sm text-[var(--mustard)]">
                  No tenants yet. <Link to="/platform-admin/tenants/new" className="text-[var(--text-ter)] hover:underline">Create one from Create Tenant</Link> first.
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={tenantAdminAdding || !tenantAdminEmail.trim() || !tenantAdminTenantId}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {tenantAdminAdding ? 'Granting…' : 'Grant Tenant Admin'}
            </button>
          </form>
          {tenantAdminError && (
            <div className="mt-3 flex items-center gap-2 text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4" />
              {tenantAdminError}
            </div>
          )}
          {tenantAdminSuccess && (
            <div className="mt-3 flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)] text-sm">
              <CheckCircle className="h-4 w-4" />
              {tenantAdminSuccess}
            </div>
          )}
        </div>
      )}

      {activeSection === 'workspace' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Grant Workspace Admin</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">
            Workspace admins can manage one workspace and invite members to it.
          </p>
          <form onSubmit={handleGrantWorkspaceAdmin} className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">User email</label>
              <input
                type="email"
                value={workspaceAdminEmail}
                onChange={(e) => setWorkspaceAdminEmail(e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Workspace</label>
              <select
                value={workspaceAdminWorkspaceId}
                onChange={(e) => setWorkspaceAdminWorkspaceId(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select workspace</option>
                {workspaces.map((w) => {
                  const tenant = tenants.find((t) => t.id === w.tenant_id);
                  return (
                    <option key={w.id} value={w.id}>
                      {w.name}{tenant ? ` (${tenant.name})` : ''}
                    </option>
                  );
                })}
              </select>
              {workspaces.length === 0 && !loadError && (
                <p className="mt-2 text-sm text-[var(--mustard)]">
                  No workspaces yet. Create a tenant first (each tenant gets a default workspace).{' '}
                  <Link to="/platform-admin/tenants/new" className="text-[var(--text-ter)] hover:underline">Create Tenant</Link>
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={workspaceAdminAdding || !workspaceAdminEmail.trim() || !workspaceAdminWorkspaceId}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {workspaceAdminAdding ? 'Granting…' : 'Grant Workspace Admin'}
            </button>
          </form>
          {workspaceAdminError && (
            <div className="mt-3 flex items-center gap-2 text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4" />
              {workspaceAdminError}
            </div>
          )}
          {workspaceAdminSuccess && (
            <div className="mt-3 flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)] text-sm">
              <CheckCircle className="h-4 w-4" />
              {workspaceAdminSuccess}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
